/**
 * Top-level routing surface for the Secret Chat SPA (task 7.1).
 *
 * `App` is the single decision point between the two screens this product
 * needs:
 *
 *   - `PasswordEntry`  — 未入室。Collects 合言葉 + ニックネーム, hashes the
 *                        password into a `roomId`, and POSTs the join
 *                        request. On success it calls `onJoined` with
 *                        `{ roomId, nickname, joinOutput }`.
 *   - `ChatView`       — 入室済み。Owns the SignalR lifecycle, message
 *                        history, and the composer for the active room.
 *                        Receives the join snapshot from the entry screen
 *                        plus a pre-computed `negotiateUserId` so the
 *                        negotiate handler's hash check succeeds.
 *
 * Routing model
 * -------------
 * Decisions live in React state, not in the URL. A page reload always
 * returns the user to PasswordEntry (Requirements R7.2 ensures the
 * nickname is auto-filled). This is intentional — the URL does not
 * encode the password, so any URL-based routing would have to re-prompt
 * for input anyway.
 *
 * Production wiring
 * -----------------
 * The collaborators (`ChatApi`, `SignalRClient`, `sha256Hex`, `storage`)
 * are built once at the top of the function and memoised so React's
 * StrictMode double-mount doesn't reset state mid-flight. The `App.test`
 * suite swaps `createChatApi` and `createSignalRClient` via `vi.mock`
 * so tests run hermetically against fakes.
 *
 * Why pre-compute `negotiateUserId` here
 * --------------------------------------
 * `api/src/functions/negotiate.ts` verifies the supplied userId equals
 * `${roomId}:${sha256Hex(nickname)}` and rejects mismatches as
 * `userIdMismatch`. Computing the hash inside `ChatView` would force the
 * child to import `lib/crypto` and run an async effect just to send
 * negotiate. Doing it once here, after the user has committed to a
 * nickname, lets ChatView stay synchronous and lets us re-use the value
 * across negotiate retries inside the same chat session.
 *
 * Requirements covered: R1.1, R4.5, R9.1.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';

import { createChatApi } from './api/chatApi';
import { API_BASE_URL } from './lib/apiBase';
import type { RoomJoinOutput } from './api/chatApi';
import { createSignalRClient } from './api/signalRClient';
import type { SignalRClient } from './api/signalRClient';

import { sha256Hex } from './lib/crypto';
import { loadNickname, saveNickname } from './lib/storage';
import type { FrontendTelemetry } from './lib/telemetry';

import { LandingPage } from './components/LandingPage';
import type { PasswordEntryJoinSuccess } from './components/PasswordEntry';
import { ChatView } from './components/ChatView';
import { Auth } from './components/Auth';
import { Dashboard } from './components/Dashboard';
import { listenToAuth } from './lib/firebase';

// ---------------------------------------------------------------------------
// Telemetry accessor
// ---------------------------------------------------------------------------
//
// `main.tsx` initialises Application Insights and stashes the client on
// `window.__telemetry`. We read it lazily here so App stays independently
// testable — the App.test fakes never touch this code path, and a missing
// global degrades to a typed no-op that the JSX layer can safely call.
//
// Direct DI through props was considered but rejected: it would force every
// component touching telemetry to thread the client through several layers
// of children, when the only callers are top-level error handlers.

const NOOP_TELEMETRY: FrontendTelemetry = {
  trackException: () => {
    /* no-op when telemetry is unavailable (e.g. in unit tests) */
  },
  trackEvent: () => {
    /* no-op */
  },
};

/**
 * Returns the process-wide telemetry client if `main.tsx` initialised one,
 * otherwise the no-op client. Wrapping the typecast keeps the read site
 * a single expression.
 */
function readTelemetry(): FrontendTelemetry {
  if (typeof window === 'undefined') return NOOP_TELEMETRY;
  const w = window as unknown as { __telemetry?: FrontendTelemetry };
  return w.__telemetry ?? NOOP_TELEMETRY;
}

// ---------------------------------------------------------------------------
// Active-session state shape
// ---------------------------------------------------------------------------

/**
 * Snapshot of "user has successfully joined" state. Carries everything
 * ChatView needs without re-fetching: the canonical roomId, the user's
 * nickname, the server's join output (participants / readReceipts /
 * lastSeq), and the pre-computed negotiate userId.
 */
interface ActiveSession {
  readonly roomId: string;
  readonly nickname: string;
  readonly joinOutput: RoomJoinOutput;
  readonly negotiateUserId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

import { useAppIcon } from './hooks/useAppIcon';
import { useTheme, ThemeSheetContext } from './hooks/useTheme';
import { ThemeSettingsSheet } from './components/ThemeSettingsSheet';

function App(): JSX.Element {
  // Initialize the app icon hook here so the DOM is updated on first load
  useAppIcon();

  // テーマ切替 (UI 刷新 §7)。マウント時に `<html data-theme>` を同期し、
  // 設定シートの開閉を Context 経由で配下に提供する。
  const { theme, setTheme } = useTheme();
  const [themeSheetOpen, setThemeSheetOpen] = useState(false);
  
  // -- Active session ----------------------------------------------------
  // `null` means "未入室"; a populated object means "入室済み". The chat
  // view is mounted only while this is non-null so its hooks (SignalR
  // connect, history fetch) run on entry and tear down on leave.
  const [session, setSession] = useState<ActiveSession | null>(null);

  // -- User Auth states --------------------------------------------------
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [view, setView] = useState<'guest' | 'auth' | 'dashboard'>('guest');

  // Monitor Auth State
  useEffect(() => {
    const unsubscribe = listenToAuth((user) => {
      setCurrentUser(user);
      setAuthInitialized(true);
      if (user) {
        setView('dashboard');
      } else {
        setView('guest');
      }
    });
    return () => unsubscribe();
  }, []);

  // -- Production collaborators -----------------------------------------
  // `useMemo` with an empty deps array gives us a stable instance for
  // the lifetime of the mounted App. The ChatApi has no internal state
  // (just a closure over baseUrl + fetch); SignalRClient owns at most
  // one HubConnection, which we tear down on leave by remounting
  // ChatView with a fresh client per session.
  const chatApi = useMemo(() => createChatApi({ baseUrl: API_BASE_URL }), []);

  // -- handleDashboardJoinRoom -------------------------------------------
  const handleDashboardJoinRoom = useCallback(
    async (roomInfo: { roomId: string; nickname: string; roomName: string }) => {
      const { roomId, nickname } = roomInfo;

      const res = await chatApi.join(roomId, { nickname });
      if (!res.ok) {
        console.error('[App] Failed to join room from dashboard', res.error);
        alert('部屋への接続に失敗しました。');
        return;
      }

      let nicknameHash: string;
      try {
        nicknameHash = await sha256Hex(nickname);
      } catch (err) {
        console.error('[App] failed to derive negotiate userId', err);
        return;
      }

      const negotiateUserId = `${roomId}:${nicknameHash}`;
      setSession({
        roomId,
        nickname,
        joinOutput: res.value,
        negotiateUserId,
      });
    },
    [chatApi]
  );

  // The storage adapter is a tiny façade over the module-level
  // `loadNickname`/`saveNickname` so PasswordEntry can stay decoupled
  // from the concrete module path (useful for tests that inject a stub).
  const storage = useMemo(
    () => ({
      loadNickname: (roomId: string) => loadNickname(roomId),
      saveNickname: (roomId: string, nickname: string) =>
        saveNickname(roomId, nickname),
    }),
    [],
  );

  // -- Per-session SignalR client ---------------------------------------
  // Tear-down semantics: we want each successful join to start from a
  // clean SignalR state. Building a fresh client per session is the
  // simplest way — the alternative (one global client, `.disconnect()`
  // on leave) leaks listener subscriptions if components forget to
  // unsubscribe. The factory returned to ChatView calls
  // `createSignalRClient()` lazily; ChatView captures the result on its
  // first mount via `useSignalRConnection`.
  const sessionRoomId = session?.roomId ?? null;
  const signalRClientFactory = useMemo<() => SignalRClient>(() => {
    return () => createSignalRClient();
    // We deliberately key the memo to `sessionRoomId` so leaving and
    // re-joining (even into the same room) produces a fresh client.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionRoomId]);

  // -- onJoined handler -------------------------------------------------
  // Called by PasswordEntry once the join request returns successfully.
  // We compute the canonical negotiate userId here (see file-level
  // comment) and commit the session in a single state update so React
  // never renders an intermediate half-baked state.
  const handleJoined = useCallback(
    async (success: PasswordEntryJoinSuccess) => {
      const { roomId, nickname, joinOutput } = success;
      // `${roomId}:${sha256Hex(nickname)}` — exactly the recipe the server's
      // negotiate handler verifies against (api/src/functions/negotiate.ts
      // buildExpectedUserId). Computing it here keeps ChatView synchronous
      // and lets the negotiate request succeed on the first attempt.
      let nicknameHash: string;
      try {
        nicknameHash = await sha256Hex(nickname);
      } catch (err) {
        // SHA-256 should not fail in modern browsers; if it does the
        // negotiate request would also fail downstream. Log and bail —
        // PasswordEntry will surface a generic error if the user retries.
        console.error('[App] failed to derive negotiate userId', err);
        // Surface to Application Insights so operators see SubtleCrypto
        // failures in the wild — they would indicate either a non-HTTPS
        // context (browser blocks Web Crypto) or a polyfill regression.
        readTelemetry().trackException(
          err instanceof Error ? err : new Error(String(err)),
          { context: 'App.handleJoined.sha256Hex' },
        );
        return;
      }
      const negotiateUserId = `${roomId}:${nicknameHash}`;
      setSession({ roomId, nickname, joinOutput, negotiateUserId });
    },
    [],
  );

  // -- onLeave handler --------------------------------------------------
  // Returning to `null` unmounts ChatView (which tears down the SignalR
  // listeners) and remounts PasswordEntry or Dashboard.
  const handleLeave = useCallback(() => {
    setSession(null);
    if (currentUser) {
      setView('dashboard');
    } else {
      setView('guest');
    }
  }, [currentUser]);

  // -- Defensive cleanup on unmount -------------------------------------
  // If the App itself unmounts (e.g. test cleanup), make sure we don't
  // leave a dangling session around in a stale closure. Pure state
  // hygiene — no real resource is owned at this scope.
  useEffect(() => {
    return () => {
      // Intentional no-op body kept for symmetry with future teardown
      // work; the `null` reset is implicit because state is per-mount.
    };
  }, []);

  // -- Render -----------------------------------------------------------
  // どの分岐でも ThemeSheetContext と ThemeSettingsSheet をマウントしたいので、
  // 1 つの内側 element を計算してから共通の wrapper で包む。
  let content: JSX.Element;
  if (!authInitialized) {
    content = (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg-plain)' }}>
        <div style={{ border: '3px solid var(--line)', borderTop: '3px solid var(--primary)', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite' }}></div>
      </div>
    );
  } else if (session !== null) {
    content = (
      <ChatView
        chatApi={chatApi}
        signalRClientFactory={signalRClientFactory}
        roomId={session.roomId}
        nickname={session.nickname}
        negotiateUserId={session.negotiateUserId}
        joinOutput={session.joinOutput}
        onLeave={handleLeave}
      />
    );
  } else if (view === 'auth') {
    content = (
      <Auth
        onAuthSuccess={(user) => {
          setCurrentUser(user);
          setView('dashboard');
        }}
        onBackToGuest={() => setView('guest')}
      />
    );
  } else if (view === 'dashboard' && currentUser) {
    content = (
      <Dashboard
        user={currentUser}
        onJoinRoom={handleDashboardJoinRoom}
      />
    );
  } else {
    content = (
      <LandingPage
        chatApi={chatApi}
        sha256Hex={sha256Hex}
        storage={storage}
        onJoined={handleJoined}
        onGoToLogin={() => setView('auth')}
      />
    );
  }

  return (
    <ThemeSheetContext.Provider value={{ open: () => setThemeSheetOpen(true) }}>
      {content}
      <ThemeSettingsSheet
        isOpen={themeSheetOpen}
        onClose={() => setThemeSheetOpen(false)}
        theme={theme}
        onChange={setTheme}
      />
    </ThemeSheetContext.Provider>
  );
}

export default App;
